import React, {
  createContext,
  useCallback,
  useContext,
  useReducer,
} from "react";
import { formatDate, getDateByTimeZone } from "@/lib/date";
import { Delivery } from "@/Models";
import { useApiStore } from "@/store/hooks";

import {
  QUERY_DATE_TYPE_RECEIPT_DATE,
  QUERY_DELIVERY_TYPE_ALL,
  QUERY_DELIVERY_TYPE_TYPE_PICKUP,
  QUERY_DELIVERY_TYPE_TYPE_RETURN,
  QUERY_DELIVERY_TYPE_AGENCY_ALLOCATION_KOOKMIN,
  AGENCY_KOOKMIN_CORPTITLE,
  REGIONS,
} from "@/store/Deliveries";

export const ALLOCATE_TYPES = {
  DELIVERY: "DELIVERY",
  PICKUP: "PICKUP",
};

export const CONSTANTS = {
  SET_COUNT: "SET_COUNT",
  SET_QUERY: "SET_QUERY",
  SET_COUNT_PARTIAL: "SET_COUNT_PARTIAL",
  SET_COUNT_DELAYED: "SET_COUNT_DELAYED",
  FETCH_ALL: "FETCH_ALL",
  FETCH_ALL_DELAYED: "FETCH_ALL_DELAYED",
  RESET: "RESET",
  RESET_QUERY: "RESET_QUERY",
};

const zonedDateToday = getDateByTimeZone();
const initMonth = formatDate(zonedDateToday, "yyyy-MM");

const INITIAL_QUERY = {
  bookId: "",
  corpUserName: "",
  dateType: QUERY_DATE_TYPE_RECEIPT_DATE,
  dateFrom: null,
  dateTo: null,
  month: initMonth,
  orderBy: "",
  orderIdFromCorp: "",
  page: 1,
  pageSize: 100,
  receiverAddressRoad: "",
  receiverDong: "",
  receiverMobile: "",
  receiverName: "",
  region: "",
  rider: "",
  pickupRiderName: "",
  deliveryRiderName: "",
  senderDong: "",
  spotName: "",
  senderName: "",
  senderMobile: "",
  queryTypes: [QUERY_DELIVERY_TYPE_ALL],
  types: "1;2;3",
};

const INITIAL_STATE = {
  count: {
    all: '-',
    receipted: '-',
    pickupScheduled: '-',
    pickupCompleted: '-',
    warehoused: '-',
    deliveryAllocated: '-',
    postponed: '-',
    deliveryStarted: '-',
    deliveryCompleted: '-',
    accident: '-',
    canceled: '-',
    typePickup: '-',
    typeReturn: '-',
    senderAddressError: '-',
    senderAddressErrorCanceled: '-',
    receiverAddressError: '-',
    receiverAddressErrorCanceled: '-',
    deliverySentBack: '-',
    deliveryLost: '-',
    mobileError: '-',
    delayed: '-',
    addressNotSupported: '-',
    addressNotSupportedCanceled: '-',
  },
  deliveries: [],
  deliveriesWholeData: [],
  pageCount: 1,
  query: INITIAL_QUERY,
  reset: false,
};

const reducer = (state, action) => {
  switch (action.type) {
    case CONSTANTS.FETCH_ALL:
      return {
        ...state,
        deliveriesWholeData: action.deliveriesWholeData,
        deliveries: action.deliveries,
        pageCount: action.pageCount,
      };
    case CONSTANTS.FETCH_ALL_DELAYED:
      return {
        ...state,
        deliveries: action.deliveries,
      };
    case CONSTANTS.RESET:
      return {
        ...state,
        reset: action.reset,
      };
    case CONSTANTS.RESET_QUERY:
      return {
        ...state,
        query: INITIAL_QUERY,
        reset: true,
      };
    case CONSTANTS.SET_COUNT:
      return {
        ...state,
        count: {
          ...action.count,
          delayed: action.countDelayed,
          all:
            action.count.receipted +
            action.count.pickupScheduled +
            action.count.pickupCompleted +
            action.count.warehoused +
            action.count.deliveryAllocated +
            action.count.postponed +
            action.count.deliveryStarted +
            action.count.deliveryCompleted,
        },
      };
    case CONSTANTS.SET_COUNT_PARTIAL:
      const updatedCount = {
        ...state.count,
        [action.key]: action.value,
      };

      if (updatedCount.receipted === '-' || updatedCount.pickupScheduled === '-' || updatedCount.pickupCompleted === '-' || updatedCount.warehoused === '-' || updatedCount.deliveryAllocated === '-' || updatedCount.postponed === '-' || updatedCount.deliveryStarted === '-' || updatedCount.deliveryCompleted === '-') {
        return {
          ...state,
          count: {
            ...updatedCount,
          },
        };
      }

      return {
        ...state,
        count: {
          ...updatedCount,
          all:
            (updatedCount.receipted ?? 0) +
            (updatedCount.pickupScheduled ?? 0) +
            (updatedCount.pickupCompleted ?? 0) +
            (updatedCount.warehoused ?? 0) +
            (updatedCount.deliveryAllocated ?? 0) +
            (updatedCount.postponed ?? 0) +
            (updatedCount.deliveryStarted ?? 0) +
            (updatedCount.deliveryCompleted ?? 0),
        },
      };
    case CONSTANTS.SET_COUNT_DELAYED:
      return {
        ...state,
        count: {
          ...state.count,
          delayed: action.countDelayed,
        },
      };
    case CONSTANTS.SET_QUERY:
      return {
        ...state,
        query: {
          ...state.query,
          ...action.query,
        },
      };
    default:
      return INITIAL_STATE;
  }
};

export const Context = createContext(INITIAL_STATE);

const makeQueryStringObj = (query = INITIAL_QUERY) => {
  const queryTypes =
    query.queryTypes.includes(QUERY_DELIVERY_TYPE_ALL) ||
    query.queryTypes.includes(QUERY_DELIVERY_TYPE_TYPE_PICKUP) ||
    query.queryTypes.includes(QUERY_DELIVERY_TYPE_TYPE_RETURN) ||
    query.queryTypes.includes(QUERY_DELIVERY_TYPE_AGENCY_ALLOCATION_KOOKMIN)
      ? ""
      : query.queryTypes.join(";");

  let types = query.queryTypes.includes(QUERY_DELIVERY_TYPE_TYPE_PICKUP)
    ? "2"
    : query.queryTypes.includes(QUERY_DELIVERY_TYPE_TYPE_RETURN)
    ? "3"
    : query.types;

  let agencyCorpUserName = "";
  if (
    query.queryTypes.includes(QUERY_DELIVERY_TYPE_AGENCY_ALLOCATION_KOOKMIN)
  ) {
    agencyCorpUserName = AGENCY_KOOKMIN_CORPTITLE;
  }

  let region = "";
  let regionType = "";

  if (query.region) {
    const regionObj = REGIONS.find((r) => r.id === query.region);

    region = regionObj.name;
    regionType = regionObj.regionType;
  }

  return {
    ...query,
    queryTypes,
    types,
    agencyCorpUserName,
    region,
    regionType,
  };
};

export const Provider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

  return (
    <Context.Provider value={{ dispatch, state }}>{children}</Context.Provider>
  );
};

export const useDeliveriesMonthlyStore = () => {
  const api = useApiStore();
  const { dispatch, state } = useContext(Context);

  const countByStatus = useCallback(
    async ({ query }) => {
      const _query = {
        ...makeQueryStringObj(query),
        riderId: query.rider ? query.rider.id : "",
      };
  
      const requests = [
        { key: "receipted",                    queryTypeForCount: "receipted",                    types: query.types },
        { key: "pickupScheduled",              queryTypeForCount: "pickupScheduled",              types: query.types },
        { key: "pickupCompleted",              queryTypeForCount: "pickupCompleted",              types: query.types },
        { key: "warehoused",                   queryTypeForCount: "warehoused",                   types: query.types },
        { key: "deliveryAllocated",            queryTypeForCount: "deliveryAllocated",            types: query.types },
        { key: "deliveryStarted",              queryTypeForCount: "deliveryStarted",              types: query.types },
        { key: "deliveryCompleted",            queryTypeForCount: "deliveryCompleted",            types: query.types },
        { key: "deliverySentBack",             queryTypeForCount: "deliverySentBack",             types: query.types },
        { key: "deliveryLost",                 queryTypeForCount: "deliveryLost",                 types: query.types },
        { key: "accident",                     queryTypeForCount: "accident",                     types: query.types },
        { key: "canceled",                     queryTypeForCount: "canceled",                     types: query.types },
        { key: "typePickup",                   types: "2" },
        { key: "typeReturn",                   types: "3" },
        { key: "postponed",                    queryTypeForCount: "postponed",                    types: query.types },
        { key: "senderAddressError",           queryTypeForCount: "senderAddressError",           types: query.types },
        { key: "senderAddressErrorCanceled",   queryTypeForCount: "senderAddressErrorCanceled",   types: query.types },
        { key: "receiverAddressError",         queryTypeForCount: "receiverAddressError",         types: query.types },
        { key: "receiverAddressErrorCanceled", queryTypeForCount: "receiverAddressErrorCanceled", types: query.types },
      ];
  
      requests.forEach(({ key, ...params }) => {
        api
          .get("/deliveries/count-by-status/by-rider-group-managers", { ..._query, ...params })
          .then((result) => dispatch({ type: CONSTANTS.SET_COUNT_PARTIAL, key, value: result }))
          .catch(() => dispatch({ type: CONSTANTS.SET_COUNT_PARTIAL, key, value: null }));
      });
  
      api
        .get("/delayed-deliveries/list/by-rider-group-managers", {
          ...makeQueryStringObj(query),
          riderId: query.rider ? query.rider.id : "",
          types: query.types,
        })
        .then((result) => dispatch({ type: CONSTANTS.SET_COUNT_DELAYED, countDelayed: result.totalCount }))
        .catch(() => dispatch({ type: CONSTANTS.SET_COUNT_DELAYED, countDelayed: null }));
  
      return true;
    },
    [api.get, dispatch],
  );

  const fetchAll = useCallback(
    async ({ query }) => {
      const { deliveries, pageCount } = await api.get(
        "/deliveries/by-rider-group-managers",
        {
          ...makeQueryStringObj(query),
          riderId: query.rider ? query.rider.id : "",
        },
      );

      dispatch({
        type: CONSTANTS.FETCH_ALL,
        deliveries: deliveries.map((d) => new Delivery(d)),
        deliveriesWholeData: deliveries,
        pageCount,
      });

      return { deliveries, pageCount };
    },
    [api.get, dispatch],
  );

  const fetchAllV2 = useCallback(
    async ({ query }) => {
      const { deliveries, pageCount } = await api.get(
        "/v2/deliveries/by-rider-group-managers",
        {
          ...makeQueryStringObj(query),
          riderId: query.rider ? query.rider.id : "",
        },
      );

      dispatch({
        type: CONSTANTS.FETCH_ALL,
        deliveries: deliveries.map((d) => new Delivery(d)),
        deliveriesWholeData: deliveries,
        pageCount,
      });

      return { deliveries, pageCount };
    },
    [api.get, dispatch],
  );

  const fetchAllDelayed = useCallback(
    async ({ query }) => {
      const deliveriesDelayed = await api.get(
        "/delayed-deliveries/list/by-rider-group-managers",
        {
          ...makeQueryStringObj(query),
          riderId: query.rider ? query.rider.id : "",
        },
      );

      dispatch({
        type: CONSTANTS.FETCH_ALL_DELAYED,
        deliveries: deliveriesDelayed.rows.map((d) => new Delivery(d)),
      });

      return { deliveriesDelayed };
    },
    [api.get, dispatch],
  );

  const resetQuery = useCallback(() => {
    dispatch({
      type: CONSTANTS.RESET_QUERY,
    });

    return INITIAL_QUERY;
  }, [dispatch]);

  const reset = useCallback(
    (reset) => {
      dispatch({
        type: CONSTANTS.RESET,
        reset,
      });
    },
    [dispatch],
  );

  const setQuery = useCallback(
    (query = {}) => {
      dispatch({
        type: CONSTANTS.SET_QUERY,
        query,
      });
    },
    [dispatch],
  );

  return {
    state,
    countByStatus,
    fetchAll,
    fetchAllV2,
    fetchAllDelayed,
    reset,
    resetQuery,
    setQuery,
  };
};
